/* ActionPrompt — the interactive layer that gates each beat of the
 * battle. Big pulsing ATTACK button when it's your turn, a smaller
 * DEFEND indicator when the opponent is acting, READY between rounds,
 * CONTINUE at the end. This is what makes the battle feel like a
 * real two-player fight — the outcome is still 100% server-decided,
 * but the pacing is now driven by your taps.
 */
import { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Shield, ChevronRight, Trophy, Skull } from 'lucide-react';

const CONFIG = {
  attack: {
    label: 'ATTACK',
    sub: 'Tap to unleash your creature',
    Icon: Swords,
    // Vivid rose/amber for offensive.
    gradient: 'from-rose-500 via-orange-500 to-amber-400',
    ring: 'rgba(251,113,133,0.55)',
  },
  defend: {
    label: 'DEFEND',
    sub: 'Brace for the incoming attack',
    Icon: Shield,
    gradient: 'from-sky-500 via-cyan-500 to-teal-400',
    ring: 'rgba(56,189,248,0.5)',
  },
  ready: {
    label: 'READY',
    sub: 'Tap for the next round',
    Icon: ChevronRight,
    gradient: 'from-emerald-500 via-teal-400 to-green-400',
    ring: 'rgba(52,211,153,0.5)',
  },
  end: {
    label: 'CONTINUE',
    sub: 'Reveal the outcome',
    Icon: Trophy,
    gradient: 'from-amber-400 via-yellow-300 to-amber-500',
    ring: 'rgba(233,196,106,0.55)',
  },
  start: {
    label: 'BEGIN',
    sub: 'Tap to open Round 1',
    Icon: Swords,
    gradient: 'from-rose-500 via-orange-500 to-amber-400',
    ring: 'rgba(251,113,133,0.5)',
  },
};

export default memo(function ActionPrompt({ kind, mySide, roundLabel, onTap }) {
  const cfg = CONFIG[kind] || CONFIG.ready;
  const Icon = cfg.Icon;
  return (
    <AnimatePresence>
      <motion.button
        key={kind + (roundLabel || '')}
        type="button"
        onClick={onTap}
        data-testid={`action-prompt-${kind}`}
        initial={{ opacity: 0, y: 40, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-x-0 bottom-24 flex flex-col items-center z-40 pointer-events-auto"
      >
        {roundLabel && (
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
            {roundLabel}
          </p>
        )}
        <motion.div
          className="relative"
          animate={kind === 'attack' || kind === 'start' || kind === 'end' ? {
            scale: [1, 1.05, 1],
          } : { scale: 1 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* Radiant pulse behind button — attracts the eye. */}
          <span
            className="absolute inset-0 rounded-full opacity-60 blur-2xl -z-10"
            style={{ background: `radial-gradient(closest-side, ${cfg.ring}, transparent)` }}
          />
          <div
            className={`relative w-52 h-14 rounded-full bg-gradient-to-r ${cfg.gradient} text-black font-bold uppercase tracking-widest flex items-center justify-center gap-2 shadow-2xl border border-white/20 active:scale-95 transition-transform`}
            style={{ boxShadow: `0 8px 30px -4px ${cfg.ring}, 0 0 0 2px rgba(255,255,255,0.12) inset` }}
          >
            <Icon className="w-5 h-5" strokeWidth={3} />
            <span className="text-base">{cfg.label}</span>
          </div>
        </motion.div>
        <p className="text-[10px] uppercase tracking-widest text-white/50 mt-2">
          {cfg.sub}
        </p>
      </motion.button>
    </AnimatePresence>
  );
});
